import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
    Envelope,
    Warning,
    ShieldCheck,
    XCircle,
    Clock,
    Trash,
    MagnifyingGlass,
    ArrowClockwise,
    CheckCircle,
} from "@phosphor-icons/react";

const HEALTH_TONE = {
    good: { label: "Healthy", bg: "bg-emerald-100", text: "text-emerald-800" },
    warning: { label: "Warning", bg: "bg-splash/20", text: "text-ink" },
    critical: { label: "Critical", bg: "bg-red-100", text: "text-red-800" },
};

const EVENT_TONE = {
    "email.bounced": "text-red-700 bg-red-50",
    "email.complained": "text-red-800 bg-red-50",
    "email.failed": "text-red-700 bg-red-50",
    "email.delivery_delayed": "text-splash bg-splash/10",
    "email.delivered": "text-emerald-700 bg-emerald-50",
    "email.opened": "text-cobalt bg-cobalt/5",
    "email.clicked": "text-cobalt bg-cobalt/5",
    "email.sent": "text-[#525252] bg-[#F5F5F5]",
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-IN", {
            day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
        });
    } catch { return "—"; }
};

export default function AdminEmailHealth() {
    const [summary, setSummary] = useState(null);
    const [events, setEvents] = useState([]);
    const [suppressions, setSuppressions] = useState([]);
    const [tab, setTab] = useState("events");
    const [eventFilter, setEventFilter] = useState("");
    const [suppQ, setSuppQ] = useState("");
    const [loading, setLoading] = useState(false);

    const loadSummary = async () => {
        try {
            const r = await api.get("/admin/email-health/summary");
            setSummary(r.data);
        } catch {
            toast.error("Failed to load email health summary");
        }
    };
    const loadEvents = async () => {
        setLoading(true);
        try {
            const params = { limit: 100 };
            if (eventFilter) params.event = eventFilter;
            const r = await api.get("/admin/email-health/events", { params });
            setEvents(r.data.items || []);
        } catch {
            toast.error("Failed to load events");
        } finally { setLoading(false); }
    };
    const loadSuppressions = async () => {
        setLoading(true);
        try {
            const r = await api.get("/admin/email-health/suppressions", {
                params: { q: suppQ, limit: 100 },
            });
            setSuppressions(r.data.items || []);
        } catch {
            toast.error("Failed to load suppressions");
        } finally { setLoading(false); }
    };

    useEffect(() => { loadSummary(); }, []);
    useEffect(() => {
        if (tab === "events") loadEvents();
        if (tab === "suppressions") loadSuppressions();
    }, [tab, eventFilter]);  // eslint-disable-line react-hooks/exhaustive-deps

    const unsuppress = async (email_masked, domain) => {
        // We only have masked email + domain client-side; the endpoint expects
        // the full address. Prompt admin to type it — deliberate friction.
        const full = window.prompt(
            `To restore delivery to this address, type the FULL email (masked shown: ${email_masked}).\n\nThis writes to the audit log.`
        );
        if (!full) return;
        try {
            await api.delete(`/admin/email-health/suppressions/${encodeURIComponent(full.trim().toLowerCase())}`);
            toast.success("Suppression removed · audit logged");
            loadSuppressions();
            loadSummary();
        } catch {
            toast.error("Failed to remove suppression");
        }
    };

    const health = summary ? HEALTH_TONE[summary.health] || HEALTH_TONE.good : null;

    return (
        <div className="w-full min-w-0 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="admin-email-health-page">
            <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#A3A3A3] mb-1">
                        Deliverability
                    </p>
                    <h1 className="font-heading text-2xl sm:text-4xl font-black tracking-tight">
                        Email health
                    </h1>
                    <p className="text-sm text-[#525252] mt-1">
                        Live bounce, complaint & suppression telemetry from Resend.
                    </p>
                </div>
                {health && (
                    <div
                        data-testid="email-health-status"
                        className={`px-4 py-2 inline-flex items-center gap-2 font-heading font-black text-xs uppercase tracking-wider ${health.bg} ${health.text}`}
                    >
                        <ShieldCheck size={14} weight="fill" /> Sender · {health.label}
                    </div>
                )}
            </div>

            {/* KPI grid */}
            {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="email-health-kpis">
                    {[
                        { label: "Sent · 30d", value: summary.sent_30d, sub: `${summary.sent_7d} in last 7d`, tone: "text-ink" },
                        { label: "Bounce rate · 30d", value: `${summary.bounce_rate_30d}%`, sub: `${summary.bounced_30d} bounces`, tone: summary.bounce_rate_30d > 5 ? "text-red-700" : summary.bounce_rate_30d > 2 ? "text-splash" : "text-emerald-600" },
                        { label: "Complaints · 30d", value: summary.complained_30d, sub: "Marked as spam", tone: summary.complained_30d > 0 ? "text-red-700" : "text-emerald-600" },
                        { label: "Active suppressions", value: summary.active_suppressions, sub: `${summary.suppressions_by_reason?.bounced || 0} bounced · ${summary.suppressions_by_reason?.complained || 0} complained`, tone: "text-cobalt" },
                    ].map((k) => (
                        <div key={k.label} className="bg-white border border-[#E5E5E5] p-5">
                            <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2">
                                {k.label}
                            </p>
                            <p className={`font-heading text-3xl font-black tracking-tighter ${k.tone}`}>
                                {k.value}
                            </p>
                            <p className="text-[11px] text-[#525252] mt-1">{k.sub}</p>
                        </div>
                    ))}
                </div>
            )}

            {/* Tabs */}
            <div className="border-b border-[#E5E5E5] mb-4 flex gap-6 overflow-x-auto">
                {[
                    { key: "events", label: "Recent events" },
                    { key: "suppressions", label: "Suppressions" },
                ].map((t) => (
                    <button
                        key={t.key}
                        onClick={() => setTab(t.key)}
                        data-testid={`email-health-tab-${t.key}`}
                        className={`text-xs font-black uppercase tracking-wider pb-3 border-b-2 transition-colors whitespace-nowrap ${
                            tab === t.key ? "border-ink text-ink" : "border-transparent text-[#A3A3A3] hover:text-ink"
                        }`}
                    >
                        {t.label}
                    </button>
                ))}
            </div>

            {tab === "events" && (
                <>
                    <div className="flex items-center gap-2 mb-4 flex-wrap">
                        {["", "email.bounced", "email.complained", "email.delivery_delayed", "email.delivered"].map((ev) => (
                            <button
                                key={ev || "all"}
                                onClick={() => setEventFilter(ev)}
                                data-testid={`email-health-filter-${ev || "all"}`}
                                className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 border transition-colors ${
                                    eventFilter === ev
                                        ? "bg-ink text-white border-ink"
                                        : "border-[#E5E5E5] text-[#525252] hover:border-ink"
                                }`}
                            >
                                {ev ? ev.replace("email.", "") : "All"}
                            </button>
                        ))}
                        <button
                            onClick={loadEvents}
                            className="ml-auto text-[#525252] hover:text-ink text-xs inline-flex items-center gap-1"
                            title="Refresh"
                        >
                            <ArrowClockwise size={12} weight="bold" /> Refresh
                        </button>
                    </div>

                    {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
                    {!loading && events.length === 0 && (
                        <div className="bg-white border border-[#E5E5E5] p-8 text-center">
                            <Envelope size={28} weight="regular" className="text-[#A3A3A3] mx-auto mb-2" />
                            <p className="text-sm text-[#525252]">No events match this filter.</p>
                        </div>
                    )}
                    {events.length > 0 && (
                        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                            {events.map((e, i) => (
                                <div key={i} data-testid={`email-event-${i}`} className="px-4 py-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex items-center gap-3">
                                        <span className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 shrink-0 ${EVENT_TONE[e.event] || "text-[#525252] bg-[#F5F5F5]"}`}>
                                            {(e.event || "").replace("email.", "")}
                                        </span>
                                        <div className="min-w-0">
                                            <p className="text-sm font-mono truncate">{e.email_masked}</p>
                                            {e.reason && <p className="text-[11px] text-[#A3A3A3]">Reason: {e.reason}</p>}
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-[#525252] shrink-0">{fmtDate(e.ts)}</p>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}

            {tab === "suppressions" && (
                <>
                    <div className="flex items-center gap-3 mb-4 flex-wrap">
                        <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-white border border-[#E5E5E5] px-3">
                            <MagnifyingGlass size={14} weight="bold" className="text-[#A3A3A3]" />
                            <input
                                value={suppQ}
                                onChange={(e) => setSuppQ(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && loadSuppressions()}
                                placeholder="Filter by email substring or domain…"
                                data-testid="email-health-supp-search"
                                className="w-full bg-transparent py-2.5 text-sm focus:outline-none placeholder:text-[#A3A3A3]"
                            />
                        </div>
                        <button
                            onClick={loadSuppressions}
                            className="border border-ink hover:bg-ink hover:text-white text-ink px-4 py-2.5 text-xs font-black uppercase tracking-wider transition-colors"
                        >
                            Search
                        </button>
                    </div>

                    <p className="text-[10px] text-[#A3A3A3] mb-3 flex items-center gap-1.5">
                        <Warning size={11} weight="fill" className="text-splash" />
                        Removing a suppression writes to the audit log and can hurt sender reputation. Only unsuppress after user re-verifies.
                    </p>

                    {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
                    {!loading && suppressions.length === 0 && (
                        <div className="bg-white border border-[#E5E5E5] p-8 text-center" data-testid="suppressions-empty">
                            <CheckCircle size={28} weight="fill" className="text-emerald-600 mx-auto mb-2" />
                            <p className="text-sm text-[#525252]">No suppressions match — clean list.</p>
                        </div>
                    )}
                    {suppressions.length > 0 && (
                        <div className="bg-white border border-[#E5E5E5] divide-y divide-[#F5F5F5]">
                            {suppressions.map((s, i) => (
                                <div key={i} data-testid={`suppression-${i}`} className="px-4 py-3 flex items-center justify-between gap-3">
                                    <div className="min-w-0 flex items-center gap-3">
                                        <XCircle size={16} weight="fill" className={s.reason === "complained" ? "text-red-700" : "text-splash"} />
                                        <div className="min-w-0">
                                            <p className="text-sm font-mono truncate">{s.email_masked}</p>
                                            <p className="text-[11px] text-[#525252]">
                                                {s.reason} · {fmtDate(s.suppressed_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => unsuppress(s.email_masked, s.email_domain)}
                                        data-testid={`suppression-unsuppress-${i}`}
                                        className="text-[10px] font-black uppercase tracking-wider text-cobalt hover:text-cobalt-dark inline-flex items-center gap-1 shrink-0"
                                        title="Remove from suppression list"
                                    >
                                        <Trash size={11} weight="bold" /> Remove
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
